import type { Database } from '@/integrations/supabase/types';

export type AdminPage = 'statistics' | 'registrations' | 'calendar' | 'export' | 'admin';
export type AdminAccessLevel = 'none' | 'read' | 'write';
export type PermissionGrid = Record<AdminPage, AdminAccessLevel>;

export const EMPTY_PERMISSION_GRID: PermissionGrid = {
  statistics: 'none',
  registrations: 'none',
  calendar: 'none',
  export: 'none',
  admin: 'none',
};

export const getPermissionFlags = (grid: PermissionGrid | null) => {
  const g = grid ?? EMPTY_PERMISSION_GRID;
  return {
    canViewStatistics: g.statistics !== 'none',
    canViewRegistrations: g.registrations !== 'none',
    canEditRegistrations: g.registrations === 'write',
    canViewCalendar: g.calendar !== 'none',
    canEditCalendar: g.calendar === 'write',
    canManageAdmins: g.admin !== 'none',
    canExport: g.export !== 'none',
  };
};

export const canAccessTab = (grid: PermissionGrid | null, tab: string): boolean => {
  const perms = getPermissionFlags(grid);
  switch (tab) {
    case 'statistics': return perms.canViewStatistics;
    case 'registrations': return perms.canViewRegistrations;
    case 'calendar': return perms.canViewCalendar;
    case 'admin-management': return perms.canManageAdmins;
    default: return false;
  }
};

// 'statistics' is deliberately last: every admin has it, so putting it first would
// make getDefaultTab always land there regardless of role. It's the intended fallback
// only for someone with no other page access, not the default for super_admin/
// registration_admin, who should still land on 'registrations' as before.
const TAB_ORDER = ['registrations', 'calendar', 'admin-management', 'statistics'];

export const getDefaultTab = (grid: PermissionGrid | null): string => {
  return TAB_ORDER.find(tab => canAccessTab(grid, tab)) ?? 'statistics';
};

export const PAGE_LABELS: Record<AdminPage, string> = {
  statistics: 'Statistiken',
  registrations: 'Anmeldungen',
  calendar: 'Kalender',
  export: 'Export',
  admin: 'Adminverwaltung',
};

export const formatPermissionSummary = (grid: PermissionGrid | null): string => {
  const g = grid ?? EMPTY_PERMISSION_GRID;
  const parts = (Object.keys(g) as AdminPage[])
    .filter(page => g[page] !== 'none')
    .map(page => g[page] === 'write' ? `${PAGE_LABELS[page]} (Schreiben)` : PAGE_LABELS[page]);
  return parts.length > 0 ? parts.join(', ') : 'Kein Zugriff';
};

// 'export' isn't a standalone page - it only adds a control inside the
// registrations tab, so having it alone must not count as usable access.
export const isAnyAccessGranted = (grid: PermissionGrid | null): boolean => {
  if (!grid) return false;
  return (Object.keys(grid) as AdminPage[])
    .filter(page => page !== 'export')
    .some(page => grid[page] !== 'none');
};
